import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) — Auditoría. */
export const auditoriaSections = [
  {
    id: "audit-kardex-eventos",
    name: "Eventos y alertas",
    type: "screen",
    route: "/audit/kardex",
    locationPath: "Auditoría → Eventos y alertas",
    generalDescription: "Monitoreo de desviaciones de inventario detectadas por reglas: saldos negativos, movimientos atípicos, etc. Cada alerta enlaza producto, almacén y documento origen.",
    prerequisites: [
      "Módulo Auditoría de inventario (kardex audit) habilitado.",
      "Acceso a Almacenes según perfil."
    ],
    interfaceElements: [
      {
        name: "Filtros estado/fecha",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: false,
        description: "Alertas abiertas o cerradas.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Tabla de alertas",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Severidad y descripción.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Marcar atendida",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Cierre operativo tras investigar.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Revise alertas nuevas diariamente.",
      "Investigue en kardex y documentos.",
      "Marque atendida con nota si aplica."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Eventos y alertas"
      },
      steps: [
        "Revise alertas nuevas diariamente.",
        "Investigue en kardex y documentos.",
        "Marque atendida con nota si aplica."
      ]
    },
    validations: ['Las reglas y tomas requieren almacén y productos válidos del kardex.'],
    expectedResult: ['Las alertas o conteos muestran diferencias coherentes con el inventario del sistema.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "audit-reglas"
    ],
    observations: [
      ROLES.limitado
    ],
    screenshots: [
      SHOTS.listado
    ],
    roles: ROLES.admin
  },

  {
    id: "audit-reglas",
    name: "Reglas de auditoría",
    type: "form",
    route: "/audit/rules",
    locationPath: "Auditoría → Reglas",
    generalDescription: "Define condiciones que generan alertas automáticas: umbrales, almacenes y productos incluidos, y frecuencia de evaluación.",
    prerequisites: [
      "Módulo Auditoría de inventario (kardex audit) habilitado.",
      "Acceso a Almacenes según perfil."
    ],
    interfaceElements: [
      {
        name: "Nombre y condición",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Lógica de la regla.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Alcance",
        controlType: "Multiselect",
        required: false,
        editable: true,
        automatic: false,
        description: "Almacenes/productos.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Destinatarios",
        controlType: "Enlace",
        required: false,
        editable: true,
        automatic: false,
        description: "Enlace a configuración de correos.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Cree regla con alcance acotado.",
      "Pruebe con movimiento controlado.",
      "Ajuste umbrales si hay ruido."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Reglas de auditoría"
      },
      steps: [
        "Cree regla con alcance acotado.",
        "Pruebe con movimiento controlado.",
        "Ajuste umbrales si hay ruido."
      ]
    },
    validations: ['Las reglas y tomas requieren almacén y productos válidos del kardex.'],
    expectedResult: ['Las alertas o conteos muestran diferencias coherentes con el inventario del sistema.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "audit-destinatarios",
      "audit-kardex-eventos"
    ],
    observations: [],
    screenshots: [
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "audit-toma-inventario",
    name: "Toma de inventario",
    type: "form",
    route: "/audit/inventory-count",
    locationPath: "Auditoría → Toma de inventario",
    generalDescription: "Planificación y ejecución de conteos físicos: crea la toma por almacén, captura cantidades y compara con saldo sistema al cerrar.",
    prerequisites: [
      "Módulo Auditoría de inventario (kardex audit) habilitado.",
      "Acceso a Almacenes según perfil."
    ],
    interfaceElements: [
      {
        name: "Almacén y fecha",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Cabecera de toma.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Grilla de productos",
        controlType: "Tabla",
        required: false,
        editable: true,
        automatic: false,
        description: "Cantidad contada.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Cerrar toma",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Calcula diferencias.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Cree toma para almacén.",
      "Ingrese conteos por producto.",
      "Cierre y revise diferencias.",
      "Genere ajustes si su rol lo permite."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Toma de inventario"
      },
      steps: [
        "Cree toma para almacén.",
        "Ingrese conteos por producto.",
        "Cierre y revise diferencias.",
        "Genere ajustes si su rol lo permite."
      ]
    },
    validations: ['Las reglas y tomas requieren almacén y productos válidos del kardex.'],
    expectedResult: ['Las alertas o conteos muestran diferencias coherentes con el inventario del sistema.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "audit-mis-tomas"
    ],
    observations: [],
    screenshots: [
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "audit-mis-tomas",
    name: "Mis tomas de inventario",
    type: "screen",
    route: "/audit/my-inventory-counts",
    locationPath: "Auditoría → Mis tomas de inventario",
    generalDescription: "Listado de tomas asignadas al usuario actual para captura en piso sin acceder a la administración global de conteos.",
    prerequisites: [
      "Módulo Auditoría de inventario (kardex audit) habilitado.",
      "Acceso a Almacenes según perfil."
    ],
    interfaceElements: [
      {
        name: "Tomas pendientes",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Estado en progreso.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Continuar conteo",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Abre captura.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Abra Mis tomas.",
      "Seleccione toma asignada.",
      "Registre cantidades y sincronice."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Mis tomas de inventario"
      },
      steps: [
        "Abra Mis tomas.",
        "Seleccione toma asignada.",
        "Registre cantidades y sincronice."
      ]
    },
    validations: ['Las reglas y tomas requieren almacén y productos válidos del kardex.'],
    expectedResult: ['Las alertas o conteos muestran diferencias coherentes con el inventario del sistema.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "audit-toma-inventario"
    ],
    observations: [
      "Pensado para contadores de campo con perfil limitado."
    ],
    screenshots: [
      SHOTS.listado
    ],
    roles: ROLES.limitado
  },

  {
    id: "audit-destinatarios",
    name: "Destinatarios",
    type: "form",
    route: "/audit/recipients",
    locationPath: "Auditoría → Destinatarios",
    generalDescription: "Correos o usuarios que reciben notificación cuando una regla de auditoría dispara alertas.",
    prerequisites: [
      "Módulo Auditoría de inventario (kardex audit) habilitado.",
      "Acceso a Almacenes según perfil."
    ],
    interfaceElements: [
      {
        name: "Correo / usuario",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Destino de notificación.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Reglas asociadas",
        controlType: "Multiselect",
        required: false,
        editable: true,
        automatic: false,
        description: "Opcional por regla.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Registre destinatarios de almacén.",
      "Asocie en cada regla.",
      "Verifique recepción con alerta de prueba."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Destinatarios"
      },
      steps: [
        "Registre destinatarios de almacén.",
        "Asocie en cada regla.",
        "Verifique recepción con alerta de prueba."
      ]
    },
    validations: ['Las reglas y tomas requieren almacén y productos válidos del kardex.'],
    expectedResult: ['Las alertas o conteos muestran diferencias coherentes con el inventario del sistema.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "audit-reglas"
    ],
    observations: [],
    screenshots: [
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  }
].map((p) => functionDoc(p));
